/**
 * AboutPage — full-bleed About Us for The Shared Origins Global Network.
 *
 * Apr 30 2026 — Lead's call: "Look at this picture does it look as
 * pretty as yours. No its white and black hard on the eye." Reverted
 * the previous light theme to the canonical dark cinematic theme
 * (obsidian background, gold heading, cyan accents, Cormorant + IBM
 * Plex Mono typography). Layout + content + testids unchanged.
 */
import { Compass, Lock, FileSearch, ScrollText, ShieldCheck, BookOpenCheck, Hammer, Map, Rocket } from 'lucide-react';
import PageNarration from './PageNarration';

const HOLDS = [
  {
    icon: ShieldCheck,
    title: 'AI moderates behavior, not ideas',
    body: "Our AI flags personal attacks, harassment, and disinformation campaigns. It does NOT decide which historical interpretation is correct. The reader stays sovereign over conclusions.",
  },
  {
    icon: Compass,
    title: 'Strike decay is rehabilitative',
    body: "When a user crosses a behavioral line, strikes attach with timestamps and decay over time. The platform welcomes people back. We are not in the cancellation business.",
  },
  {
    icon: FileSearch,
    title: 'The evidence log is sovereign',
    body: "Every claim links to a source. Every source has a citation. Every citation is auditable. Disagreement happens in the margins of evidence, not over whether evidence exists.",
  },
  {
    icon: Lock,
    title: 'The platform defends its users',
    body: "Your data lives where you say it lives. Export anytime. Delete anytime. The platform makes money from subscriptions, not from selling your reading habits.",
  },
  {
    icon: ScrollText,
    title: 'The platform does not narrate stories',
    body: "We surface evidence, contradictions, and patterns. We do not tell you what to think. The chains belong to history; the conclusions belong to you.",
  },
];

const NEXT_BLOCKS = [
  {
    icon: Hammer,
    title: 'Built on the contest clock',
    body: "TSOGN was built by one person — Midnite — over the run-up to the Demo Day window. Every visible feature you click ships from a real codebase you can audit.",
  },
  {
    icon: Map,
    title: 'The 4-chain spine is the proof',
    body: "Walker → Toolmaker → Spark → Symbolic Leap is the deep-time spine that anchors the platform. Every other chain we publish hangs off this skeleton with the same methodology applied.",
  },
  {
    icon: Rocket,
    title: 'Kickstarter pending LLC + domain',
    body: "Launch backed by a Kickstarter campaign for the public launch. Stripe layer waiting on LLC paperwork to clear. The platform ships first; the storefront opens second.",
  },
];

export default function AboutPage() {
  return (
    <div className="px-4 md:px-8 lg:px-12 py-10 md:py-16 max-w-5xl mx-auto" data-testid="about-page">
      {/* ── Block 1: what TSOGN is ─────────────────────────────────────── */}
      <header className="mb-12 md:mb-16">
        <div className="flex items-start justify-between gap-4 mb-3">
          <p className="font-mono text-[10px] md:text-xs tracking-[0.3em] uppercase text-[#2F8A92]/85" data-testid="about-eyebrow">
            The Shared Origins Global Network
          </p>
          <PageNarration pageKey="about" label="Listen · About" />
        </div>
        <h1 className="font-historical text-[#D4A84A] text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] mb-5" data-testid="about-headline">
          History with the receipts.
        </h1>
        <p className="font-historical text-white/85 text-lg md:text-xl leading-relaxed max-w-3xl">
          TSOGN is a sourced-only history platform. Every claim links to peer-reviewed evidence.
          Contradictions are surfaced, not papered over. The reader is sovereign — we don&apos;t tell you
          what to think, we hand you the chain and stand back.
        </p>
      </header>

      {/* ── Block 2: the five methodology holds ─────────────────────── */}
      <section className="mb-14 md:mb-20" data-testid="about-methodology">
        <p className="font-mono text-[10px] md:text-xs tracking-[0.25em] uppercase text-[#D4A84A] mb-2">⟨ Methodology — five holds ⟩</p>
        <h2 className="font-historical text-white/95 text-2xl md:text-3xl font-light tracking-tight mb-7">
          The principles we don&apos;t break — even when it&apos;s inconvenient.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {HOLDS.map((h, i) => {
            const Icon = h.icon;
            return (
              <div
                key={h.title}
                className="border border-[#D4A84A]/15 rounded-md p-5 hover:border-[#D4A84A]/45 transition-colors"
                style={{ background: 'rgba(255,255,255,0.02)' }}
                data-testid={`about-hold-${i}`}
              >
                <div className="flex items-start gap-3">
                  <Icon size={18} className="text-[#D4A84A] mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-historical text-[#D4A84A] text-base md:text-lg leading-tight mb-1.5">{h.title}</div>
                    <p className="font-mono text-xs md:text-sm text-white/75 leading-relaxed">{h.body}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Block 3: who built it ───────────────────────────────────── */}
      <section className="mb-14 md:mb-20 border-l-2 border-[#D4A84A] pl-5 md:pl-7" data-testid="about-builder">
        <p className="font-mono text-[10px] md:text-xs tracking-[0.25em] uppercase text-[#D4A84A] mb-2">⟨ Built by ⟩</p>
        <h2 className="font-historical text-white text-2xl md:text-3xl font-light tracking-tight mb-3">Midnite Productions LLC</h2>
        <p className="font-historical text-white/85 text-base md:text-lg leading-relaxed max-w-3xl mb-3">
          A one-person studio out of California. Tradesman by background, builder by instinct. TSOGN
          started as a question — <span className="text-[#D4A84A] italic">&ldquo;why does every history book leave out the contradictions?&rdquo;</span> — and turned
          into a platform when the answer kept being <span className="italic">&ldquo;because somebody decided the story for you&rdquo;</span>.
        </p>
        <p className="font-historical text-white/65 text-base md:text-lg leading-relaxed max-w-3xl">
          The methodology came first. The code came second. The Kickstarter comes third.
        </p>
      </section>

      {/* ── Block 4: what's next ────────────────────────────────────── */}
      <section className="mb-10 md:mb-14" data-testid="about-roadmap">
        <p className="font-mono text-[10px] md:text-xs tracking-[0.25em] uppercase text-[#2F8A92] mb-2">⟨ What&apos;s next ⟩</p>
        <h2 className="font-historical text-white/95 text-2xl md:text-3xl font-light tracking-tight mb-7">The build cadence after Demo Day.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {NEXT_BLOCKS.map((n) => {
            const Icon = n.icon;
            return (
              <div
                key={n.title}
                className="border border-[#2F8A92]/15 rounded-md p-5 hover:border-[#2F8A92]/45 transition-colors"
                style={{ background: 'rgba(255,255,255,0.02)' }}
              >
                <Icon size={18} className="text-[#2F8A92] mb-3" />
                <div className="font-historical text-white text-base md:text-lg leading-tight mb-1.5">{n.title}</div>
                <p className="font-mono text-xs text-white/65 leading-relaxed">{n.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Footer kicker ──────────────────────────────────────────── */}
      <footer className="border-t border-[#D4A84A]/15 pt-8 md:pt-10 mt-8 flex items-center gap-3 text-xs md:text-sm text-white/55 font-mono" data-testid="about-footer">
        <BookOpenCheck size={14} className="text-[#D4A84A]" />
        <span>
          Got a source we missed? A real correction? Real concern?
          <a href="mailto:sales@midniteproductions.org" className="text-[#2F8A92] hover:text-[#D4A84A] ml-2 underline decoration-dotted">Write us</a>.
          A real human reads it.
        </span>
      </footer>
    </div>
  );
}
